/**
 * Loans help text, search words and common tasks for the redesigned portal shell (admin 0.53.0).
 * Kept beside loansPages.ts (LOP's registry) rather than inside it, so the registry stays LOP's
 * contract; keyed by the same paths. Wording follows each page's own introduction.
 */

import type { ShellTask } from '@retail-ops/panel-shell';
import { LOANS_BASE } from './loansPages';

export const LOANS_HOME_HELP =
  'Loans administration: finding write-ups, watching approvals and scheduled jobs, Encompass configuration and Core diagnostics. Every change asks for a reason and is recorded with your name, the time, and the before and after values.';

export const LOANS_GUIDE: Readonly<Record<string, { help: string; synonyms?: ReadonlyArray<string> }>> = {
  [`${LOANS_BASE}/find/records`]: {
    help: 'Type a number (a write-up id like 4572 or WU-4572). Every list that uses that number is searched: write-ups, the archive, adverse actions, withdrawn and drafts. A write-up opens in the pipeline dashboard.',
    synonyms: ['write-up', 'wu', 'loan number', 'look up', 'search'],
  },
  [`${LOANS_BASE}/monitoring/approvals`]: {
    help: 'Every loan approval request (CLO, executive and supervisor signatures) across all officers: who asked, who must answer, and how it settled. Read-only; an approval is settled from its Teams card or the write-up.',
    synonyms: ['clo', 'approval', 'signature request', 'resend card'],
  },
  [`${LOANS_BASE}/monitoring/jobs`]: {
    help: 'The write-up jobs the loans API runs on a schedule: the mode each is in (live, dry run, manual or off), when it last ran and what it did, and anything waiting on a decision.',
    synonyms: ['detector', 'scheduled', 'pause', 'resume', 'nightly'],
  },
  [`${LOANS_BASE}/configuration/encompass`]: {
    help: 'Choose which Encompass milestone files the post-closing task, one template at a time. A template with no post-closing milestone, or one that is switched off, never files a task.',
    synonyms: ['milestone', 'post-closing', 'webhook', 'template'],
  },
  [`${LOANS_BASE}/configuration/settings`]: {
    help: 'Every setting the loans API reads, in plain words and grouped by what it affects. Open one to see what it does, the values it takes, what to check before changing it and the page that manages it. Settings that cannot be changed here say why. Credentials are never shown. A change takes effect the next time the API reads the setting (within a few minutes).',
    synonyms: ['options', 'switches', 'configuration', 'watcher', 'shadow', 'live', 'encompass', 'boarding', 'e-mail'],
  },
  [`${LOANS_BASE}/configuration/notices`]: {
    help: 'A notice opens on top of the loans apps for everyone until they click "Got it" or the notice ends. Use it for planned outages and major releases. Times are Central.',
    synonyms: ['outage', 'banner', 'announcement', 'splash'],
  },
  [`${LOANS_BASE}/access/people`]: {
    help: "Who can do what in the loans apps. A person's personas decide what they see and can do; a loans admin grant decides what they can change here. Each change is recorded with your name and reason.",
    synonyms: ['personas', 'pipeline access', 'permissions', 'roles', 'grant'],
  },
  [`${LOANS_BASE}/tools/jx-probe`]: {
    help: 'Read-only inquiries against Core Director through the loans API. Only inquiry and search operations are allowed, and customer data in the answer is masked on the server.',
    synonyms: ['core', 'jxchange', 'inquiry', 'diagnostics'],
  },
  [`${LOANS_BASE}/tools/test-card`]: {
    help: 'Sends a real approval card from the Loans Chat Bot to your own everyday Teams account. Press Approve or Reject there and the card changes to the decided card, as a real approval does, but nothing real is approved and nobody else is told.',
    synonyms: ['bot test', 'test card', 'teams card', 'chat bot', 'loans bot', 'approval card', 'card test', 'agents sdk'],
  },
};

/** Common tasks; each shows only when its page is in the person's capability answer. */
export const LOANS_TASKS: ReadonlyArray<ShellTask> = [
  {
    id: 'loans-access',
    title: 'Give someone pipeline access',
    blurb: 'Give a person a persona, or change the ones they have.',
    path: `${LOANS_BASE}/access/people`,
    icon: 'user-check',
    crumb: 'Access and roles › People and personas',
    synonyms: ['grant', 'add person', 'permissions'],
  },
  {
    id: 'loans-postclosing',
    title: 'Change the post-closing trigger',
    blurb: 'Which Encompass milestone files the post-closing task, per template.',
    path: `${LOANS_BASE}/configuration/encompass`,
    icon: 'chart',
    crumb: 'Configuration › Encompass milestones and webhook',
  },
  {
    id: 'loans-notice',
    title: 'Post a planned-outage notice',
    blurb: 'Show everyone in the loans apps a message for a set window.',
    path: `${LOANS_BASE}/configuration/notices`,
    icon: 'message',
    crumb: 'Configuration › Splash notices',
    synonyms: ['outage', 'release'],
  },
  {
    id: 'loans-find',
    title: 'Look up a write-up',
    blurb: 'By number, across write-ups, the archive, adverse actions, withdrawn and drafts.',
    path: `${LOANS_BASE}/find/records`,
    icon: 'search',
    crumb: 'Find › Find a record',
  },
  {
    id: 'loans-setting',
    title: 'Change a loans setting',
    blurb: 'Settings grouped by area; the ones that cannot be changed here say why.',
    path: `${LOANS_BASE}/configuration/settings`,
    icon: 'sliders',
    crumb: 'Configuration › Loans settings',
  },
  {
    id: 'loans-test-card',
    title: 'Send me a test approval card',
    blurb: 'Check the Loans Chat Bot: a test card with Approve and Reject arrives in your Teams.',
    path: `${LOANS_BASE}/tools/test-card`,
    icon: 'message',
    crumb: 'Tools › Test approval card',
    synonyms: ['bot test', 'teams card', 'chat bot'],
  },
  {
    id: 'loans-job',
    title: 'Pause or resume a job',
    blurb: 'See each scheduled job and switch its mode between off, dry run, manual and live.',
    path: `${LOANS_BASE}/monitoring/jobs`,
    icon: 'clock',
    crumb: 'Monitoring › Scheduled jobs',
    synonyms: ['detector', 'stop job'],
  },
];
